/**
 * Mutations for the franchise_exception_tags lifecycle, routed through
 * the unified matrix-driven endpoints
 * ``POST /admin/exceptions/{id}/transition`` (single-row) and
 * ``POST /admin/exceptions/transition/bulk`` (batched).
 *
 * Callers issue ``transition(tagId, action, reason?)`` or
 * ``bulkTransition(tagIds, action, reason?)`` and consume the full
 * envelope. ``applied`` distinguishes a real mutation from an
 * idempotent no-op (e.g. Reopen against a tuple that already has a
 * fresh open sibling row); callers use it to suppress UX side effects
 * on no-ops.
 *
 * RBAC: callers should gate the UI surface with
 * ``canBlockExceptions(user)`` from ``services/auth`` (the same role
 * set covers ignore). The server is the source of truth; the UI check
 * is a convenience.
 *
 * ``pending`` + ``error`` are shared across every method so a button
 * row can disable all actions during any in-flight mutation.
 */
import { useCallback, useState } from 'react'
import { apiClient } from '../services/api'
import type { TransitionAction } from '../config/exceptions/shared/transitions'

export type ExceptionTagResponse = {
  id: number
  fp_id: number
  data_domain: string
  object_id: string
  exception_type_id: number
  detected_at: string | null
  last_detected_at: string | null
  resolved_at: string | null
  ignored_at: string | null
  ignored_by_user_id: number | null
  ignore_reason: string | null
  blocked_at: string | null
  blocked_by_user_id: number | null
  block_reason: string | null
  marked_fixed_at?: string | null
  marked_fixed_by_user_id?: number | null
  status: 'needs_review' | 'blocked' | 'resolved' | 'ignored'
  /**
   * False when the server short-circuited because the requested
   * transition would have been a no-op (e.g. unignore against a tuple
   * that already has a fresh open row). The response still carries the
   * canonical current tag, so callers can refresh UI without a follow-
   * up fetch. Backward-compatible: defaults to true on the server, so
   * older callers that ignore it keep working.
   */
  applied?: boolean
}

export interface BulkActionResultItem {
  tag_id: number
  ok: boolean
  error?: string | null
  /** False when this row was a no-op (already at target state). */
  applied?: boolean | null
}

export interface BulkActionResponse {
  success: number
  failed: number
  results: BulkActionResultItem[]
}

/** Outcome of marking every open exception on one object fixed. */
export interface MarkFixedByObjectResult {
  /** Newly stamped this call. */
  marked: number
  /** Open siblings that already carried the marker (left untouched). */
  already_fixed: number
  /** Every open sibling on the object. */
  total_open: number
  /** The open sibling tag ids. */
  tag_ids: number[]
}

/**
 * Envelope returned by POST /admin/exceptions/{id}/transition.
 *
 * ``tag`` is the canonical current lifecycle row for the tuple
 * AFTER the transition (or before, on a no-op). ``applied`` is
 * False when the server short-circuited (already at target state).
 * ``target_status`` echoes the matrix target so the FE can compare
 * against ``tag.status`` without re-deriving.
 */
export interface ExceptionTransitionResponse {
  tag: ExceptionTagResponse
  applied: boolean
  target_status: 'needs_review' | 'blocked' | 'resolved' | 'ignored'
}

/**
 * The fixed marker is ORTHOGONAL to the status lifecycle - it sets/clears
 * ``marked_fixed_at`` on the open tag row WITHOUT changing status (a
 * Needs-review row can be marked fixed). It POSTs to dedicated endpoints, NOT
 * the transition matrix, so it never appears in the status menu. Phase 1 is
 * DQ-only; the server re-gates by role.
 */
export type AnnotationAction = 'mark-fixed' | 'unmark-fixed'

export interface UseExceptionLifecycleResult {
  pending: boolean
  error: string | null
  /**
   * Apply any lifecycle transition via the unified endpoint. The
   * server matrix decides whether the action is valid for the tag's
   * current status and which mutation kind (update / insert_new /
   * insert_new_then_update) to dispatch. The response carries the
   * canonical current tag for the tuple, so callers never thread a
   * "new id" through follow-up requests.
   */
  transition: (
    tagId: number,
    action: TransitionAction,
    reason?: string,
  ) => Promise<ExceptionTransitionResponse>
  /**
   * Apply a lifecycle transition to up to 200 rows in one request.
   * The server runs the same matrix dispatch per id as the single-row
   * endpoint, with role + market gating per id, and returns per-row
   * ``{ok, error, applied}``.
   */
  bulkTransition: (
    tagIds: number[],
    action: TransitionAction,
    reason?: string,
  ) => Promise<BulkActionResponse>
  /**
   * Set/clear the fixed marker (mark/unmark fixed) on a single open tag.
   * Returns the canonical tag row (carrying the updated ``marked_fixed_at``) so
   * callers can refresh without a follow-up fetch. ``applied`` is false on a
   * no-op.
   */
  annotate: (
    tagId: number,
    action: AnnotationAction,
  ) => Promise<ExceptionTagResponse & { applied?: boolean }>
  /**
   * Mark EVERY open exception on one object fixed in a single call (the
   * sibling axis - e.g. a sale flagged for both a payment- and a status-
   * mismatch). The server resolves the object's open tags in the given
   * ``dataDomain`` and stamps the fixed marker on each, returning the
   * outcome counts so the caller can toast + refresh.
   */
  markFixedByObject: (args: {
    fpCode: string
    dataDomain: string
    objectId: string
    /** Drawer's focused code. Sent so the server can prefix-match the whole
     *  product family for the composite per-outlet stock codes (stamp every
     *  affected outlet, not just the representative). */
    exceptionCode?: string
  }) => Promise<MarkFixedByObjectResult>
}

export const useExceptionLifecycle = (): UseExceptionLifecycleResult => {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handle = useCallback(
    async <T,>(fn: () => Promise<{ data: T }>): Promise<T> => {
      setPending(true)
      setError(null)
      try {
        const res = await fn()
        return res.data
      } catch (err: any) {
        const detail =
          err?.response?.data?.detail || err?.message || 'Request failed'
        setError(String(detail))
        throw err
      } finally {
        setPending(false)
      }
    },
    [],
  )

  const transition = useCallback(
    (tagId: number, action: TransitionAction, reason?: string) =>
      handle(() =>
        apiClient.post<ExceptionTransitionResponse>(
          `/admin/exceptions/${tagId}/transition`,
          reason ? { action, reason } : { action },
        ),
      ),
    [handle],
  )

  const bulkTransition = useCallback(
    (tagIds: number[], action: TransitionAction, reason?: string) =>
      handle(() =>
        apiClient.post<BulkActionResponse>(
          '/admin/exceptions/transition/bulk',
          reason
            ? { tag_ids: tagIds, action, reason }
            : { tag_ids: tagIds, action },
        ),
      ),
    [handle],
  )

  const annotate = useCallback(
    (tagId: number, action: AnnotationAction) =>
      handle(() =>
        apiClient.post<ExceptionTagResponse & { applied?: boolean }>(
          `/admin/exceptions/${tagId}/${action}`,
        ),
      ),
    [handle],
  )

  const markFixedByObject = useCallback(
    (args: {
      fpCode: string
      dataDomain: string
      objectId: string
      exceptionCode?: string
    }) =>
      handle(() =>
        apiClient.post<MarkFixedByObjectResult>(
          '/admin/exceptions/mark-fixed/by-object',
          {
            fp_code: args.fpCode,
            data_domain: args.dataDomain,
            object_id: args.objectId,
            exception_code: args.exceptionCode,
          },
        ),
      ),
    [handle],
  )

  return {
    pending,
    error,
    transition,
    bulkTransition,
    annotate,
    markFixedByObject,
  }
}
